import { useState } from 'react';
import {
  Clock,
  User,
  Receipt,
  UtensilsCrossed,
  Coffee,
  Truck,
  ChefHat,
  CheckCircle2,
  XCircle,
  MapPin,
  Gift,
  Mail,
  Printer,
  Ban,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Banknote,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

type OrderStatus = 'open' | 'sent' | 'in_progress' | 'ready' | 'completed' | 'cancelled' | 'refunded';
type OrderType = 'dine_in' | 'takeout' | 'delivery' | 'bar_tab' | 'online';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
  notes?: string | null;
  modifiers?: Array<{ id: string; name: string; price: number }> | unknown;
  status: string;
}

interface Payment {
  id: string;
  payment_method: string;
  amount: number;
  tip_amount?: number;
  created_at: string;
}

interface Order {
  id: string;
  order_number: string;
  order_type: OrderType;
  status: OrderStatus;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  tip_amount: number;
  total: number;
  notes?: string | null;
  guest_count: number;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  table?: { name: string } | null;
  customer?: { first_name: string | null; last_name: string | null } | null;
  server?: { first_name: string | null; last_name: string | null; avatar_url?: string | null } | null;
  items?: OrderItem[];
  payments?: Payment[];
  location?: { id: string; name: string } | null;
}

interface OrderDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  onPrintReceipt?: () => void;
  onGiftReceipt?: () => void;
  onEmailReceipt?: () => void;
  onVoid?: () => void;
  onReturnItems?: () => void;
}

const statusConfig: Record<OrderStatus, { label: string; color: string; icon: typeof Clock }> = {
  open: { label: 'Open', color: 'bg-gray-500', icon: Clock },
  sent: { label: 'Sent to Kitchen', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'Preparing', color: 'bg-blue-500', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500', icon: CheckCircle2 },
  completed: { label: 'Completed', color: 'bg-gray-500', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle },
  refunded: { label: 'Refunded', color: 'bg-orange-500', icon: XCircle },
};

const typeConfig: Record<OrderType, { label: string; icon: typeof Coffee }> = {
  dine_in: { label: 'Dine-in', icon: Coffee },
  takeout: { label: 'Takeout', icon: UtensilsCrossed },
  delivery: { label: 'Delivery', icon: Truck },
  bar_tab: { label: 'Bar Tab', icon: Coffee },
  online: { label: 'Online', icon: Coffee },
};

export function OrderDetailsDialog({
  open,
  onOpenChange,
  order,
  onPrintReceipt,
  onGiftReceipt,
  onEmailReceipt,
  onVoid,
  onReturnItems,
}: OrderDetailsDialogProps) {
  const { formatCurrency } = useCurrency();
  const [isExpanded, setIsExpanded] = useState(true);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!order) return null;

  const status = order.status as OrderStatus;
  const orderType = order.order_type as OrderType;
  const StatusIcon = statusConfig[status]?.icon || Clock;
  const TypeIcon = typeConfig[orderType]?.icon || Coffee;

  const customerName = order.customer
    ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
    : null;

  const cashierName = order.server
    ? `${order.server.first_name || ''} ${order.server.last_name || ''}`.trim()
    : null;

  const cashierInitials = order.server
    ? `${order.server.first_name?.[0] || ''}${order.server.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  const items = order.items || [];
  const payments = order.payments || [];

  // Calculate total items quantity
  const totalItemsCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate balance
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balance = order.total - totalPaid;

  // Parse modifiers safely
  const getModifiers = (item: OrderItem): Array<{ name: string; price: number }> => {
    if (!item.modifiers) return [];
    if (Array.isArray(item.modifiers)) {
      return item.modifiers as Array<{ name: string; price: number }>;
    }
    return [];
  };

  // Get payment method icon
  const getPaymentIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'cash':
        return Banknote;
      case 'card':
      case 'credit':
      case 'debit':
        return CreditCard;
      default:
        return CreditCard;
    }
  };

  // Get payment method label
  const getPaymentLabel = (method: string) => {
    switch (method.toLowerCase()) {
      case 'cash':
        return 'Cash';
      case 'card':
        return 'Card';
      case 'credit':
        return 'Credit Card';
      case 'debit':
        return 'Debit Card';
      case 'gift_card':
        return 'Gift Card';
      default:
        return method;
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="lg" className="max-h-[90vh] overflow-hidden flex flex-col">
        <ResponsiveModalHeader className="border-b pb-4">
          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-4">
              {/* Receipt Number and Date */}
              <div>
                <ResponsiveModalTitle className="text-xl font-semibold">
                  {order.order_number}
                </ResponsiveModalTitle>
                <ResponsiveModalDescription className="text-sm text-muted-foreground">
                  {formatDate(order.created_at)} at {formatTime(order.created_at)}
                </ResponsiveModalDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Customer Badge */}
              {customerName ? (
                <Badge variant="secondary" className="gap-1.5">
                  <User className="h-3 w-3" />
                  {customerName}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">-</Badge>
              )}
              {/* Cashier with Avatar */}
              {cashierName && (
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {cashierInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-sm">
                    <p className="font-medium">{cashierName}</p>
                    <p className="text-xs text-muted-foreground">
                      {order.location?.name || 'Unknown Location'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="flex-1 overflow-hidden p-0">
          <div className="flex gap-6 h-full p-6">
            {/* Left Column - Sale Details */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Expandable Sale Details Header */}
              <button
                className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors shrink-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <span className="font-medium">Sale details</span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {isExpanded && (
                <div className="flex flex-col min-h-0 flex-1 mt-4">
                  {/* Scrollable Items Section */}
                  <div className="overflow-y-auto max-h-[300px] space-y-3 pr-2">
                    {/* Order Items */}
                    {items.map((item) => {
                      const modifiers = getModifiers(item);
                      const taxInfo = '(No Tax)'; // You can calculate this based on your tax logic
                      
                      return (
                        <div
                          key={item.id}
                          className={cn(
                            'flex justify-between p-3 rounded-lg border bg-card',
                            item.status === 'voided' && 'opacity-50'
                          )}
                        >
                          <div className="flex-1">
                            <div className="flex items-start gap-2">
                              <span className="font-semibold text-primary">{item.quantity}</span>
                              <div>
                                <p className={cn(
                                  "font-medium underline decoration-dotted cursor-pointer hover:text-primary",
                                  item.status === 'voided' && 'line-through'
                                )}>
                                  {item.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Sold by: {cashierName || 'Unknown'}
                                </p>
                                {modifiers.length > 0 && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {modifiers.map((mod, idx) => (
                                      <span key={idx}>
                                        + {mod.name}
                                        {mod.price > 0 && ` (+${formatCurrency(mod.price)})`}
                                        {idx < modifiers.length - 1 && ', '}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {item.notes && (
                                  <p className="text-xs text-muted-foreground mt-1 italic">
                                    Note: {item.notes}
                                  </p>
                                )}
                                {item.status === 'voided' && (
                                  <Badge variant="destructive" className="text-xs mt-1">
                                    Voided
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">@ {formatCurrency(item.unit_price)}</p>
                            <p className="text-xs text-muted-foreground">
                              + {formatCurrency(0)} Tax {taxInfo}
                            </p>
                            <p className="font-semibold mt-1">{formatCurrency(item.total)}</p>
                          </div>
                        </div>
                      );
                    })}

                    {/* Order Notes */}
                    {order.notes && (
                      <div className="p-3 rounded-lg border bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-1">Note</p>
                        <p className="text-sm">{order.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Fixed Totals Section */}
                  <div className="shrink-0 mt-4 space-y-3">
                    <Separator />

                    {/* Totals */}
                    <div className="space-y-2 px-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatCurrency(order.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total tax (No Tax)</span>
                        <span>{formatCurrency(order.tax_amount)}</span>
                      </div>
                      {order.discount_amount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Discount</span>
                          <span>-{formatCurrency(order.discount_amount)}</span>
                        </div>
                      )}
                      {order.tip_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Tip</span>
                          <span>{formatCurrency(order.tip_amount)}</span>
                        </div>
                      )}
                      <Separator />
                      <div className="flex justify-between text-lg font-bold">
                        <span>SALE TOTAL</span>
                        <span>{formatCurrency(order.total)}</span>
                      </div>
                    </div>

                    {/* Payment Details */}
                    {payments.length > 0 && (
                      <>
                        <Separator />
                        <div className="space-y-2 px-3">
                          {payments.map((payment, idx) => {
                            const PaymentIcon = getPaymentIcon(payment.payment_method);
                            return (
                              <div key={payment.id || idx} className="flex justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <PaymentIcon className="h-4 w-4 text-muted-foreground" />
                                  <span>{getPaymentLabel(payment.payment_method)}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-muted-foreground text-xs mr-2">
                                    {formatDateTime(payment.created_at)}
                                  </span>
                                  <span className="font-medium">{formatCurrency(payment.amount)}</span>
                                </div>
                              </div>
                            );
                          })}
                          {/* Change/Balance */}
                          {balance < 0 && (
                            <div className="flex justify-between text-sm">
                              <span>{getPaymentLabel(payments[0]?.payment_method || 'Cash')}</span>
                              <span>{formatCurrency(balance)}</span>
                            </div>
                          )}
                          <Separator />
                          <div className="flex justify-between font-medium">
                            <span>Balance</span>
                            <span>{formatCurrency(Math.max(0, balance))}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Actions */}
            <div className="w-48 space-y-2 shrink-0">
              {onReturnItems && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={onReturnItems}
                >
                  <RotateCcw className="h-4 w-4" />
                  Return items
                </Button>
              )}
              {onGiftReceipt && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={onGiftReceipt}
                >
                  <Gift className="h-4 w-4" />
                  Gift receipt
                </Button>
              )}
              {onEmailReceipt && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={onEmailReceipt}
                >
                  <Mail className="h-4 w-4" />
                  Email receipt
                </Button>
              )}
              {onPrintReceipt && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={onPrintReceipt}
                >
                  <Printer className="h-4 w-4" />
                  Print receipt
                </Button>
              )}
              {onVoid && !['cancelled', 'refunded'].includes(status) && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={onVoid}
                >
                  <Ban className="h-4 w-4" />
                  Void
                </Button>
              )}
            </div>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="gap-1">
                  <TypeIcon className="h-3 w-3" />
                  {typeConfig[orderType]?.label || orderType}
                </Badge>
              </div>
              {order.table?.name && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  Table {order.table.name}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Receipt className="h-3.5 w-3.5" />
                {totalItemsCount} item{totalItemsCount !== 1 ? 's' : ''}
              </div>
              <Badge
                className={cn(
                  'gap-1',
                  statusConfig[status]?.color || 'bg-gray-500',
                  'text-white'
                )}
              >
                <StatusIcon className="h-3 w-3" />
                {statusConfig[status]?.label || status}
              </Badge>
            </div>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
